import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { all_routes } from '../../router/all_routes';
import Table from '../../../core/common/dataTable';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import FinancialYearModal from './FyYearFormModal';
import { filterData } from '../../../core/common/GlobalFunctions/tableFilters';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../core/common/GlobalFunctions/exportTableData';
import { fetchAllFinancialYears, fetchFinancialYearsByCompanyId } from '../../../core/data/redux/financialYear/financialYearActions';
import { useAppConfig } from '../../../customhooks/useAppConfig';
import { formatDate } from '../../../core/utils/configFormatters';
import StatusFilterHeader from '../../../core/common/commonComponents/StatusFilterHeader';
import { changeUserCompanyPreference, fetchActiveCompanyForUser } from '../../../core/data/redux/company/companyDetailsActions';
const FinancialYear = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('active');
    const [selectedSort, setSelectedSort] = useState<any>('');
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { financialYears = [], loading } = useSelector((state: any) => state.financialYears);
    const { companyDetails = [] } = useSelector((state: any) => state.companyDetails || []);
    const { activeCompany } = useSelector((state: any) => state.companyDetails);
    const { user } = useSelector((state: any) => state.auth);
    const [loadingFYId, setLoadingFYId] = useState<number | null>(null);
    const companyId = activeCompany?.company_detail_id
    const handleChangeFinancialYear = async (financialYearId: number) => {
        if (!user?.UserId) return;
        setLoadingFYId(financialYearId);
        try {
            await dispatch(changeUserCompanyPreference({
                userId: user.UserId,
                financial_year_id: financialYearId,
            })).unwrap();
            dispatch(fetchFinancialYearsByCompanyId(companyId));
            if (user?.UserId) {
                dispatch(fetchActiveCompanyForUser(user.UserId));
            }
            setTableKey(prev => prev + 1);
        } finally {
            setLoadingFYId(null);
        }
    };
    useEffect(() => {
        if (companyDetails.length === 0) return;

        dispatch(fetchAllFinancialYears())

    }, [companyDetails, dispatch]);

    const handleOpenModal = (type: 'edit' | 'create', data?: any) => {
        setModalType(type);
        setSelectedRowData(data || null);
        setIsModalVisible(true);
    };

    const filteredData = useMemo(() => {
        const filters = selectedStatus && selectedStatus !== "All" ? { status: selectedStatus } : {};

        return filterData(financialYears || [], filters, "created_at", "created_at", selectedSort).map((item: any) => ({
            ...item,
            start_date: item.start_date && item.start_date.split("T")[0],
            end_date: item.end_date && item.end_date.split("T")[0],
            created_at: item.created_at && item.created_at.split("T")[0],
            updated_at: item.updated_at && item.updated_at.split("T")[0],
        }));
    }, [financialYears, selectedStatus, selectedSort]);


    useEffect(() => {
        setTableKey(prev => prev + 1);
    }, [selectedStatus, selectedSort, financialYears]);

    const config = useAppConfig();
    if (!config) return null;

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            sorter: (a: any, b: any) => a.name.localeCompare(b.name),
        },
        {
            title: "Start Date",
            dataIndex: "start_date",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
        },
        {
            title: "End Date",
            dataIndex: "end_date",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime(),
        },
        {
            title: "Company",
            dataIndex: "company_name",
            sorter: (a: any, b: any) => a.company_name.localeCompare(b.company_name),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) =>
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        },
        {
            title: "Updated At",
            dataIndex: "updated_at",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) =>
                new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (_: string, record: any) => {
                const isActive = record.id === activeCompany?.financial_year_id;
                const isLoading = loadingFYId === record.id;

                return (
                    <button
                        className="btn btn-sm btn-primary d-flex align-items-center"
                        disabled={isActive || isLoading}
                        onClick={() => handleChangeFinancialYear(record.id)}
                    >
                        {isLoading && (
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                        )}
                        {isActive ? 'Active' : 'Set Active'}
                    </button>
                );
            },
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (_: string, record: any) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => handleOpenModal("edit", record)}
                    >
                        <i className="ti ti-edit" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div>
                            <h2 className="mb-1">Financial Year Settings</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Administration</li>
                                    <li className="breadcrumb-item active">Financial Year Settings</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex align-items-center flex-wrap">
                            <div className="dropdown me-2">
                                <Link to="#" className="dropdown-toggle btn btn-white" data-bs-toggle="dropdown">
                                    <i className="ti ti-file-export me-1" /> Export
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                        <button onClick={() => exportToCSV(filteredData, 'financial_year_data')} className="dropdown-item">
                                            Export as CSV
                                        </button>
                                    </li>
                                    <li>
                                        <button onClick={() => exportToExcel(filteredData, 'financial_year_data')} className="dropdown-item">
                                            Export as Excel
                                        </button>
                                    </li>
                                    <li>
                                        <button onClick={() => exportToPDF(filteredData, 'financial_year_data')} className="dropdown-item">
                                            Export as PDF
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            <button className="btn btn-primary d-flex align-items-center" onClick={() => handleOpenModal('create')}>
                                <i className="ti ti-circle-plus me-2" />  Add Financial Year
                            </button>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* <div className="row mb-4">{getStatusCards(financialYears, "FinancialYearStatus")}</div> */}
                    {/* Filters */}
                    <div className="card">
                        <StatusFilterHeader
                            title="Financial Year List"
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedSort={selectedSort}
                            setSelectedSort={setSelectedSort}
                        />
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="footer d-sm-flex justify-content-between bg-white p-3 border-top">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By <Link to="#" className="text-primary">Leadchain Systems</Link>
                    </p>
                </div>
            </div>

            <FinancialYearModal
                modalType={modalType}
                isModalVisible={isModalVisible}
                handleCancel={() => setIsModalVisible(false)}
                setTableKey={setTableKey}
                selectedRowData={selectedRowData}
                companyId={companyId}
            />
        </>
    );
};

export default FinancialYear;
